import { useState, useEffect, useMemo, useCallback } from "react";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Label } from "@/components/ui/label";
import { Search, Filter, X, ArrowUpDown, Package, Users, MapPin, DollarSign, CalendarDays, Settings2, Check, Store, UserCircle, TrendingUp, Calendar } from "lucide-react";
import { toast } from "sonner";
import axios from "axios";
import { DropdownMenu, DropdownMenuContent, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import LocalSalidasModal from "@/components/LocalSalidasModal";
import LocalStoreModal from "@/components/LocalStoreModal";
import TCBadge from "@/components/TCBadge";

import { API_URL as API } from "@/lib/api";

const SALIDAS_COLUMNS = [
  { key: "fecha", label: "Fecha", default: true },
  { key: "codigo", label: "Codigo", default: true },
  { key: "descripcion", label: "Descripcion", default: true },
  { key: "cantidad", label: "Cant.", default: true },
  { key: "stock", label: "Stock", default: true },
  { key: "pmenor", label: "PMenor", default: true },
  { key: "destino", label: "Destino", default: true },
  { key: "tipo_venta", label: "T.Venta", default: true },
  { key: "cliente", label: "Cliente", default: true },
  { key: "familia", label: "Familia", default: false },
  { key: "subfamilia", label: "SubFam.", default: false },
  { key: "unidad", label: "Unidad", default: false },
  { key: "pmayor", label: "PMayor", default: false },
  { key: "proveedor", label: "Proveedor", default: false },
  { key: "entrega", label: "Entrega", default: false },
  { key: "recibe", label: "Recibe", default: false },
];

const SalidasDashboard = ({ userRole = "admin", globalTC }) => {
  const [entregas, setEntregas] = useState([]);
  const [stats, setStats] = useState({});
  const [filterOptions, setFilterOptions] = useState({ familias: [], subfamilias: [], clientes: [], destinos: [], tipos_venta: [] });
  const [searchTerm, setSearchTerm] = useState("");
  const [filters, setFilters] = useState({ familia: "", subfamilia: "", cliente: "", destino: "", tipo_venta: "", fechaDesde: "", fechaHasta: "" });
  const [showFilters, setShowFilters] = useState(false);
  const [loading, setLoading] = useState(true);
  const [sortConfig, setSortConfig] = useState({ key: null, direction: 'asc' });

  const MONETARY_COLS = ["pmenor", "pmayor"];
  const availableColumns = userRole === "admin" ? SALIDAS_COLUMNS : SALIDAS_COLUMNS.filter(c => !MONETARY_COLS.includes(c.key));
  const [tipoCambio, setTipoCambio] = useState(9.4);
  const [visibleCols, setVisibleCols] = useState(() =>
    availableColumns.filter(c => c.default).map(c => c.key)
  );
  const [modalCamila, setModalCamila] = useState(false);
  const [modalMonica, setModalMonica] = useState(false);
  const [modalOtros, setModalOtros] = useState(false);
  const [salPeriod, setSalPeriod] = useState("month");
  const [salByLocal, setSalByLocal] = useState(null);
  const [salLoading, setSalLoading] = useState(false);

  const toggleColumn = (key) => {
    setVisibleCols(prev => prev.includes(key) ? prev.filter(k => k !== key) : [...prev, key]);
  };
  const isVisible = (key) => visibleCols.includes(key);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const [entRes, statsRes, filtersRes] = await Promise.all([
        axios.get(`${API}/api/entregas/all`),
        axios.get(`${API}/api/entregas/stats`),
        axios.get(`${API}/api/entregas/filters`),
      ]);
      setEntregas(entRes.data.entregas || []);
      setStats(statsRes.data);
      setFilterOptions(filtersRes.data);
    } catch (err) {
      toast.error("Error al cargar salidas");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  // Fetch salidas per local by period
  const fetchSalByLocal = useCallback(async (p) => {
    setSalLoading(true);
    try {
      const res = await axios.get(`${API}/api/entregas/per-local-period?period=${p}`);
      setSalByLocal(res.data);
    } catch { /* silently fail */ }
    finally { setSalLoading(false); }
  }, []);

  useEffect(() => { fetchSalByLocal(salPeriod); }, [salPeriod, fetchSalByLocal]);

  const filteredEntregas = useMemo(() => {
    let filtered = entregas;
    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      filtered = filtered.filter(e => e.codigo?.toLowerCase().includes(term) || e.descripcion?.toLowerCase().includes(term));
    }
    if (filters.familia) filtered = filtered.filter(e => e.familia === filters.familia);
    if (filters.subfamilia) filtered = filtered.filter(e => e.subfamilia === filters.subfamilia);
    if (filters.cliente) filtered = filtered.filter(e => e.cliente === filters.cliente);
    if (filters.destino) filtered = filtered.filter(e => e.destino === filters.destino);
    if (filters.tipo_venta) filtered = filtered.filter(e => e.tipo_venta === filters.tipo_venta);
    if (filters.fechaDesde) filtered = filtered.filter(e => e.fecha >= filters.fechaDesde);
    if (filters.fechaHasta) filtered = filtered.filter(e => e.fecha <= filters.fechaHasta);
    return filtered;
  }, [entregas, searchTerm, filters]);

  const sortedEntregas = useMemo(() => {
    if (!sortConfig.key) return filteredEntregas;
    return [...filteredEntregas].sort((a, b) => {
      const aVal = a[sortConfig.key], bVal = b[sortConfig.key];
      if (typeof aVal === 'string') return sortConfig.direction === 'asc' ? aVal.localeCompare(bVal) : bVal.localeCompare(aVal);
      return sortConfig.direction === 'asc' ? (aVal || 0) - (bVal || 0) : (bVal || 0) - (aVal || 0);
    });
  }, [filteredEntregas, sortConfig]);

  const availableSubfamilias = useMemo(() => {
    if (!filters.familia) return [];
    return [...new Set(entregas.filter(e => e.familia === filters.familia).map(e => e.subfamilia).filter(Boolean))].sort();
  }, [entregas, filters.familia]);

  const totalValorFiltrado = useMemo(
    () => filteredEntregas.reduce((sum, e) => sum + (e.cantidad || 0) * (e.pmayor || 0), 0), [filteredEntregas]
  );

  // Local breakdowns
  const entregasCamila = useMemo(() => entregas.filter(e => (e.recibe || '').toUpperCase() === 'CAMILA'), [entregas]);
  const entregasMonica = useMemo(() => entregas.filter(e => (e.recibe || '').toUpperCase() === 'MONICA'), [entregas]);
  const entregasOtros = useMemo(() => entregas.filter(e => {
    const r = (e.recibe || '').toUpperCase();
    return r !== 'CAMILA' && r !== 'MONICA';
  }), [entregas]);

  const formatUSD = (v) => {
    const tc = tipoCambio || 1;
    return '$ ' + new Intl.NumberFormat('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(v / tc);
  };

  const handleSort = (key) => {
    setSortConfig(prev => ({ key, direction: prev.key === key && prev.direction === 'asc' ? 'desc' : 'asc' }));
  };

  const handleClear = () => {
    setFilters({ familia: "", subfamilia: "", cliente: "", destino: "", tipo_venta: "", fechaDesde: "", fechaHasta: "" });
    setSearchTerm("");
    toast.success("Filtros limpiados");
  };

  const handleFilterChange = (key, value) => {
    const val = value === "__all__" ? "" : value;
    if (key === "familia") setFilters(prev => ({ ...prev, familia: val, subfamilia: "" }));
    else setFilters(prev => ({ ...prev, [key]: val }));
  };

  const hasActiveFilters = filters.familia || filters.subfamilia || filters.cliente || filters.destino || filters.tipo_venta || filters.fechaDesde || filters.fechaHasta || searchTerm;

  const formatPrice = (v) => new Intl.NumberFormat('es-BO', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(v) + ' Bs';

  const formatDate = (d) => {
    if (!d) return '';
    const parts = d.split('-');
    if (parts.length === 3) return `${parts[2]}/${parts[1]}/${parts[0]}`;
    return d;
  };

  const allMetrics = [
    { title: "Total Salidas", value: stats.total_entregas || 0, icon: Package, color: "text-[#FF9900]", bg: "bg-[#FF9900]/10", monetary: false },
    { title: "Valor PMayor", value: formatPrice(totalValorFiltrado), usd: formatUSD(totalValorFiltrado), icon: DollarSign, color: "text-emerald-600", bg: "bg-emerald-50", monetary: true },
    { title: "Unidades Movidas", value: stats.total_cantidad || 0, icon: ArrowUpDown, color: "text-amber-600", bg: "bg-amber-50", monetary: false },
    { title: "Clientes", value: stats.clientes_unicos || 0, icon: Users, color: "text-violet-600", bg: "bg-violet-50", monetary: false },
    { title: "Destinos", value: stats.destinos_unicos || 0, icon: MapPin, color: "text-rose-600", bg: "bg-rose-50", monetary: false },
  ];

  const metrics = userRole === "admin" ? allMetrics : allMetrics.filter(m => !m.monetary);

  const SortHeader = ({ field, label }) => (
    <TableHead className="body-font font-semibold">
      <Button variant="ghost" size="sm" onClick={() => handleSort(field)} className="body-font text-xs" data-testid={`sort-salidas-${field}`}>
        {label} <ArrowUpDown className="ml-1 h-3 w-3" />
      </Button>
    </TableHead>
  );

  return (
    <div className="space-y-4" data-testid="salidas-dashboard">
      {/* Metrics */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
        {metrics.map((m, i) => (
          <Card key={i} className="p-3 border border-border shadow-sm" data-testid={`salidas-metric-${i}`}>
            <div className="flex items-center gap-3">
              <div className={`p-2 rounded-lg ${m.bg}`}><m.icon className={`h-4 w-4 ${m.color}`} /></div>
              <div>
                <p className="body-font text-xs text-muted-foreground">{m.title}</p>
                <p className="heading-font text-lg font-bold text-foreground">{typeof m.value === 'number' ? m.value.toLocaleString() : m.value}</p>
                {m.usd && <p className="mono-font text-xs text-[#FF9900] mt-0.5" data-testid={`salidas-metric-${i}-usd`}>{m.usd}</p>}
              </div>
            </div>
          </Card>
        ))}
      </div>

      {/* TC Badge - readonly for all roles */}
      {globalTC !== null && globalTC !== undefined && (
        <div className="flex justify-end">
          <TCBadge tc={globalTC} />
        </div>
      )}

      {/* Salidas per Local - Period Cards */}
      {userRole === "admin" && (
        <Card className="p-4 border border-border shadow-sm" data-testid="salidas-period-cards">
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-2">
              <TrendingUp className="h-4 w-4 text-[#FF9900]" />
              <h3 className="heading-font text-sm font-bold text-foreground">Salidas por Local</h3>
            </div>
            <div className="flex items-center gap-1 bg-muted rounded-lg p-0.5">
              {[
                { key: "week", label: "Semana" },
                { key: "month", label: "Mes" },
                { key: "year", label: "Año" },
              ].map(p => (
                <button key={p.key} onClick={() => setSalPeriod(p.key)}
                  className={`px-3 py-1.5 text-xs font-medium rounded-md transition-all ${
                    salPeriod === p.key
                      ? "bg-[#FF9900] text-white shadow-sm"
                      : "text-muted-foreground hover:text-foreground"
                  }`}
                  data-testid={`sal-period-${p.key}`}>
                  {p.label}
                </button>
              ))}
            </div>
          </div>

          {salLoading ? (
            <div className="flex items-center justify-center py-6">
              <div className="animate-spin h-5 w-5 border-2 border-[#FF9900] border-t-transparent rounded-full" />
            </div>
          ) : salByLocal?.locals ? (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              {/* Manco Kapac */}
              <div className="flex items-stretch gap-3 p-3.5 rounded-lg border-2 border-amber-200 bg-amber-50/30" data-testid="sal-local-manco">
                <div className="flex flex-col items-center justify-center px-2">
                  <div className="p-2 rounded-xl bg-amber-100"><Store className="h-5 w-5 text-amber-700" /></div>
                </div>
                <div className="flex-1 min-w-0">
                  <p className="heading-font text-sm font-bold text-foreground">Manco Kapac</p>
                  <div className="grid grid-cols-2 gap-x-4 gap-y-1 mt-1.5">
                    <div>
                      <p className="text-[10px] text-muted-foreground uppercase">Salidas</p>
                      <p className="mono-font text-sm font-bold text-foreground">{(salByLocal.locals.manco_kapac?.salidas_count || 0).toLocaleString()}</p>
                    </div>
                    <div>
                      <p className="text-[10px] text-muted-foreground uppercase">Unidades</p>
                      <p className="mono-font text-sm font-bold text-foreground">{(salByLocal.locals.manco_kapac?.total_qty || 0).toLocaleString()}</p>
                    </div>
                    <div>
                      <p className="text-[10px] text-muted-foreground uppercase">Valor Bs</p>
                      <p className="mono-font text-xs font-bold text-amber-700">{formatPrice(salByLocal.locals.manco_kapac?.total_valor_bs || 0)}</p>
                    </div>
                    <div>
                      <p className="text-[10px] text-muted-foreground uppercase">Valor USD</p>
                      <p className="mono-font text-xs font-bold text-[#FF9900]">$ {(salByLocal.locals.manco_kapac?.total_valor_usd || 0).toLocaleString('en-US', { minimumFractionDigits: 2 })}</p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Plaza Bonita */}
              <div className="flex items-stretch gap-3 p-3.5 rounded-lg border-2 border-violet-200 bg-violet-50/30" data-testid="sal-local-plaza">
                <div className="flex flex-col items-center justify-center px-2">
                  <div className="p-2 rounded-xl bg-violet-100"><Store className="h-5 w-5 text-violet-700" /></div>
                </div>
                <div className="flex-1 min-w-0">
                  <p className="heading-font text-sm font-bold text-foreground">Plaza Bonita</p>
                  <div className="grid grid-cols-2 gap-x-4 gap-y-1 mt-1.5">
                    <div>
                      <p className="text-[10px] text-muted-foreground uppercase">Salidas</p>
                      <p className="mono-font text-sm font-bold text-foreground">{(salByLocal.locals.plaza_bonita?.salidas_count || 0).toLocaleString()}</p>
                    </div>
                    <div>
                      <p className="text-[10px] text-muted-foreground uppercase">Unidades</p>
                      <p className="mono-font text-sm font-bold text-foreground">{(salByLocal.locals.plaza_bonita?.total_qty || 0).toLocaleString()}</p>
                    </div>
                    <div>
                      <p className="text-[10px] text-muted-foreground uppercase">Valor Bs</p>
                      <p className="mono-font text-xs font-bold text-violet-700">{formatPrice(salByLocal.locals.plaza_bonita?.total_valor_bs || 0)}</p>
                    </div>
                    <div>
                      <p className="text-[10px] text-muted-foreground uppercase">Valor USD</p>
                      <p className="mono-font text-xs font-bold text-[#FF9900]">$ {(salByLocal.locals.plaza_bonita?.total_valor_usd || 0).toLocaleString('en-US', { minimumFractionDigits: 2 })}</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          ) : null}

          {salByLocal?.from_date && (
            <p className="text-[10px] text-muted-foreground mt-2 flex items-center gap-1">
              <Calendar className="h-3 w-3" /> Desde: {salByLocal.from_date}
            </p>
          )}
        </Card>
      )}

      {/* Local Buttons */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
        <button onClick={() => setModalCamila(true)}
          className="group flex items-center gap-3 p-3.5 rounded-lg border-2 border-amber-200 bg-amber-50/50 hover:bg-amber-100/70 hover:border-amber-300 transition-all text-left"
          data-testid="btn-local-camila">
          <div className="p-2.5 rounded-xl bg-amber-100 group-hover:bg-amber-200 transition-colors">
            <Store className="h-5 w-5 text-amber-700" />
          </div>
          <div className="flex-1 min-w-0">
            <p className="heading-font text-sm font-bold text-foreground">Camila - Manco Kapac</p>
            <p className="mono-font text-xs text-muted-foreground mt-0.5">
              {entregasCamila.length} salidas{userRole === "admin" ? <> &middot; {formatPrice(entregasCamila.reduce((s, e) => s + (e.cantidad || 0) * (e.pmayor || 0), 0))}</> : null}
            </p>
          </div>
          <span className="text-xs text-amber-600 font-medium group-hover:translate-x-0.5 transition-transform">Ver &rarr;</span>
        </button>

        <button onClick={() => setModalMonica(true)}
          className="group flex items-center gap-3 p-3.5 rounded-lg border-2 border-violet-200 bg-violet-50/50 hover:bg-violet-100/70 hover:border-violet-300 transition-all text-left"
          data-testid="btn-local-monica">
          <div className="p-2.5 rounded-xl bg-violet-100 group-hover:bg-violet-200 transition-colors">
            <Store className="h-5 w-5 text-violet-700" />
          </div>
          <div className="flex-1 min-w-0">
            <p className="heading-font text-sm font-bold text-foreground">Monica - Plaza Bonita</p>
            <p className="mono-font text-xs text-muted-foreground mt-0.5">
              {entregasMonica.length} salidas{userRole === "admin" ? <> &middot; {formatPrice(entregasMonica.reduce((s, e) => s + (e.cantidad || 0) * (e.pmayor || 0), 0))}</> : null}
            </p>
          </div>
          <span className="text-xs text-violet-600 font-medium group-hover:translate-x-0.5 transition-transform">Ver &rarr;</span>
        </button>

        <button onClick={() => setModalOtros(true)}
          className="group flex items-center gap-3 p-3.5 rounded-lg border-2 border-border bg-muted/50 hover:bg-muted/70 hover:border-border transition-all text-left"
          data-testid="btn-local-otros">
          <div className="p-2.5 rounded-xl bg-muted group-hover:bg-muted transition-colors">
            <UserCircle className="h-5 w-5 text-muted-foreground" />
          </div>
          <div className="flex-1 min-w-0">
            <p className="heading-font text-sm font-bold text-foreground">Otros</p>
            <p className="mono-font text-xs text-muted-foreground mt-0.5">
              {entregasOtros.length} salidas{userRole === "admin" ? <> &middot; {formatPrice(entregasOtros.reduce((s, e) => s + (e.cantidad || 0) * (e.pmayor || 0), 0))}</> : null}
            </p>
          </div>
          <span className="text-xs text-muted-foreground font-medium group-hover:translate-x-0.5 transition-transform">Ver &rarr;</span>
        </button>
      </div>

      {/* Modals */}
      <LocalStoreModal open={modalCamila} onOpenChange={setModalCamila}
        localId="manco_kapac" localName="Camila - Manco Kapac" accentColor="text-amber-600" tipoCambio={tipoCambio} userRole={userRole} />
      <LocalStoreModal open={modalMonica} onOpenChange={setModalMonica}
        localId="plaza_bonita" localName="Monica - Plaza Bonita" accentColor="text-violet-600" tipoCambio={tipoCambio} userRole={userRole} />
      <LocalSalidasModal open={modalOtros} onOpenChange={setModalOtros}
        title="Otros" subtitle="Salidas a letreros, internet y otros destinos"
        entregas={entregasOtros} tipoCambio={tipoCambio} accentColor="text-muted-foreground" icon={UserCircle} />

      {/* Search & Toggle Filters */}
      <Card className="p-4 border border-border shadow-sm">
        <div className="flex gap-3 items-center">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input data-testid="salidas-search" value={searchTerm} onChange={e => setSearchTerm(e.target.value)}
              placeholder="Buscar por codigo o descripcion..." className="pl-10 body-font" />
          </div>
          <Button variant="outline" onClick={() => setShowFilters(!showFilters)} data-testid="salidas-toggle-filters"
            className={showFilters ? "border-[#FF9900] text-[#FF9900]" : ""}>
            <Filter className="h-4 w-4 mr-2" /> Filtros
            {hasActiveFilters && <Badge className="ml-2 bg-[#FF9900]/15 text-[#FF9900] text-xs">Activos</Badge>}
          </Button>
          {hasActiveFilters && (
            <Button variant="ghost" onClick={handleClear} data-testid="salidas-clear-filters"><X className="h-4 w-4 mr-1" /> Limpiar</Button>
          )}
        </div>
      </Card>

      {/* Filters */}
      {showFilters && (
        <Card className="p-4 border border-border shadow-sm" data-testid="salidas-filters-bar">
          <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-3">
            <div>
              <Label className="body-font text-xs mb-1.5 block">Familia</Label>
              <Select value={filters.familia} onValueChange={v => handleFilterChange("familia", v)}>
                <SelectTrigger className="h-9 text-xs"><SelectValue placeholder="Todas" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="__all__">Todas</SelectItem>
                  {filterOptions.familias.filter(Boolean).map(f => <SelectItem key={f} value={f}>{f}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="body-font text-xs mb-1.5 block">SubFamilia</Label>
              <Select value={filters.subfamilia} onValueChange={v => handleFilterChange("subfamilia", v)} disabled={!filters.familia}>
                <SelectTrigger className="h-9 text-xs"><SelectValue placeholder={filters.familia ? "Todas" : "Familia primero"} /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="__all__">Todas</SelectItem>
                  {availableSubfamilias.map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="body-font text-xs mb-1.5 block">Cliente</Label>
              <Select value={filters.cliente} onValueChange={v => handleFilterChange("cliente", v)}>
                <SelectTrigger className="h-9 text-xs"><SelectValue placeholder="Todos" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="__all__">Todos</SelectItem>
                  {filterOptions.clientes.filter(Boolean).map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="body-font text-xs mb-1.5 block">Destino</Label>
              <Select value={filters.destino} onValueChange={v => handleFilterChange("destino", v)}>
                <SelectTrigger className="h-9 text-xs"><SelectValue placeholder="Todos" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="__all__">Todos</SelectItem>
                  {filterOptions.destinos.filter(Boolean).map(d => <SelectItem key={d} value={d}>{d}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="body-font text-xs mb-1.5 block">Tipo Venta</Label>
              <Select value={filters.tipo_venta} onValueChange={v => handleFilterChange("tipo_venta", v)}>
                <SelectTrigger className="h-9 text-xs"><SelectValue placeholder="Todos" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="__all__">Todos</SelectItem>
                  {filterOptions.tipos_venta.filter(Boolean).map(t => <SelectItem key={t} value={t}>{t}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="body-font text-xs mb-1.5 block">Fecha Desde</Label>
              <Input type="date" value={filters.fechaDesde} onChange={e => handleFilterChange("fechaDesde", e.target.value)}
                className="h-9 text-xs" data-testid="salidas-fecha-desde" />
            </div>
            <div>
              <Label className="body-font text-xs mb-1.5 block">Fecha Hasta</Label>
              <Input type="date" value={filters.fechaHasta} onChange={e => handleFilterChange("fechaHasta", e.target.value)}
                className="h-9 text-xs" data-testid="salidas-fecha-hasta" />
            </div>
          </div>
        </Card>
      )}

      {/* Table */}
      <Card className="border border-border shadow-sm" data-testid="salidas-table-card">
        <div className="p-4 border-b border-border flex justify-between items-center">
          <h2 className="heading-font text-lg font-semibold text-foreground">Salidas</h2>
          <div className="flex items-center gap-3">
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="outline" size="sm" className="text-xs" data-testid="salidas-column-visibility-toggle">
                  <Settings2 className="h-3.5 w-3.5 mr-1.5" /> Columnas ({visibleCols.length})
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-48 p-2 max-h-80 overflow-y-auto">
                {availableColumns.map(col => (
                  <button key={col.key} onClick={() => toggleColumn(col.key)}
                    className="flex items-center w-full px-2 py-1.5 text-xs rounded hover:bg-muted transition-colors"
                    data-testid={`salidas-col-toggle-${col.key}`}>
                    <div className={`w-4 h-4 mr-2 rounded border flex items-center justify-center ${
                      isVisible(col.key) ? 'bg-[#FF9900] border-sky-600' : 'border-border'
                    }`}>
                      {isVisible(col.key) && <Check className="h-3 w-3 text-white" />}
                    </div>
                    {col.label}
                  </button>
                ))}
              </DropdownMenuContent>
            </DropdownMenu>
            <CalendarDays className="h-4 w-4 text-muted-foreground" />
            <p className="body-font text-sm text-muted-foreground" data-testid="salidas-count">
              Mostrando {filteredEntregas.length} de {entregas.length} salidas
            </p>
          </div>
        </div>
        <div className="overflow-x-auto custom-scrollbar">
          <Table>
            <TableHeader className="sticky top-0 bg-muted/95 backdrop-blur z-10">
              <TableRow>
                {isVisible("fecha") && <SortHeader field="fecha" label="Fecha" />}
                {isVisible("codigo") && <SortHeader field="codigo" label="Codigo" />}
                {isVisible("descripcion") && <SortHeader field="descripcion" label="Descripcion" />}
                {isVisible("cantidad") && <SortHeader field="cantidad" label="Cant." />}
                {isVisible("stock") && <SortHeader field="stock" label="Stock" />}
                {isVisible("pmenor") && <SortHeader field="pmenor" label="PMenor" />}
                {isVisible("destino") && <SortHeader field="destino" label="Destino" />}
                {isVisible("tipo_venta") && <TableHead className="body-font font-semibold text-xs">T.Venta</TableHead>}
                {isVisible("cliente") && <SortHeader field="cliente" label="Cliente" />}
                {isVisible("familia") && <TableHead className="body-font font-semibold text-xs">Familia</TableHead>}
                {isVisible("subfamilia") && <TableHead className="body-font font-semibold text-xs">SubFam.</TableHead>}
                {isVisible("unidad") && <TableHead className="body-font font-semibold text-xs">Unidad</TableHead>}
                {isVisible("pmayor") && <SortHeader field="pmayor" label="PMayor" />}
                {isVisible("proveedor") && <TableHead className="body-font font-semibold text-xs">Proveedor</TableHead>}
                {isVisible("entrega") && <TableHead className="body-font font-semibold text-xs">Entrega</TableHead>}
                {isVisible("recibe") && <TableHead className="body-font font-semibold text-xs">Recibe</TableHead>}
              </TableRow>
            </TableHeader>
            <TableBody>
              {loading ? (
                <TableRow><TableCell colSpan={visibleCols.length} className="text-center py-8"><p className="body-font text-muted-foreground">Cargando salidas...</p></TableCell></TableRow>
              ) : sortedEntregas.length === 0 ? (
                <TableRow><TableCell colSpan={visibleCols.length} className="text-center py-8"><p className="body-font text-muted-foreground">No se encontraron salidas</p></TableCell></TableRow>
              ) : (
                sortedEntregas.map((e, i) => (
                  <TableRow key={i} className="hover:bg-muted table-row-hover" data-testid={`salida-row-${i}`}>
                    {isVisible("fecha") && <TableCell className="mono-font text-xs font-medium text-[#FF9900]" data-testid={`salida-fecha-${i}`}>{formatDate(e.fecha)}</TableCell>}
                    {isVisible("codigo") && <TableCell className="mono-font text-xs font-medium" data-testid={`salida-codigo-${i}`}>{e.codigo}</TableCell>}
                    {isVisible("descripcion") && <TableCell className="body-font text-xs">
                      <span className="block max-w-xs leading-snug line-clamp-2">{e.descripcion}</span>
                    </TableCell>}
                    {isVisible("cantidad") && <TableCell className="mono-font text-xs text-center font-bold">{e.cantidad}</TableCell>}
                    {isVisible("stock") && <TableCell className="mono-font text-xs text-center">{e.stock}</TableCell>}
                    {isVisible("pmenor") && <TableCell className="mono-font text-xs">{formatPrice(e.pmenor)}</TableCell>}
                    {isVisible("destino") && <TableCell className="body-font text-xs">{e.destino && <Badge variant="outline" className="text-xs">{e.destino}</Badge>}</TableCell>}
                    {isVisible("tipo_venta") && <TableCell className="body-font text-xs">{e.tipo_venta && <Badge className="bg-indigo-50 text-indigo-700 border-indigo-200 text-xs border">{e.tipo_venta}</Badge>}</TableCell>}
                    {isVisible("cliente") && <TableCell className="body-font text-xs font-medium">{e.cliente}</TableCell>}
                    {isVisible("familia") && <TableCell className="body-font text-xs text-muted-foreground">{e.familia}</TableCell>}
                    {isVisible("subfamilia") && <TableCell className="body-font text-xs text-muted-foreground">{e.subfamilia}</TableCell>}
                    {isVisible("unidad") && <TableCell className="body-font text-xs text-muted-foreground">{e.unidad}</TableCell>}
                    {isVisible("pmayor") && <TableCell className="mono-font text-xs text-muted-foreground">{formatPrice(e.pmayor)}</TableCell>}
                    {isVisible("proveedor") && <TableCell className="body-font text-xs text-muted-foreground">{e.proveedor}</TableCell>}
                    {isVisible("entrega") && <TableCell className="body-font text-xs text-muted-foreground">{e.entrega}</TableCell>}
                    {isVisible("recibe") && <TableCell className="body-font text-xs text-muted-foreground">{e.recibe}</TableCell>}
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </div>
      </Card>
    </div>
  );
};

export default SalidasDashboard;
